import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { hasFilterSelectedValue } from '../../../../components/filters/hooks/common.hook';
import { FILTER_NAMES } from '../../../../components/filters/hooks/filter-names.constants';
import { LoadingComponent } from '../../../../components/loading/loading.component';
import { PreloaderComponent } from '../../../../components/loading/preloader.component';
import { ReportsComponentProps } from '../../../../components/reports/interfaces/reports-component-props';
import { useDataProvider } from '../../../../hooks/data-provider/data-provider.hook';
import {
  OPERATIONS,
  VALUE_MATCH,
} from '../../../../hooks/filters/interfaces/filter-selected-value.interface';
import { useAppFilterSelectedValue } from '../../../../hooks/filters/use-app-filter-selected-value.hook';
import BackToDemandComponent from '../back-to-demand.component';
import { CoefficientCardComponent } from './coefficient-card.component';
import './coefficient.component.scss';

interface CoefficientComponentProps
  extends ReportsComponentProps,
    PropsWithChildren {
  title: string;
  coefficientHelpData?: Record<string, any>;
}
export interface CoefficientHelp {
  name: string;
  description: string;
}
const matchStrategy = {
  operation: OPERATIONS.AND,
  valueMatch: VALUE_MATCH.MULTIPLE,
};
// Cards that we don't want to disply by default
const TO_REMOVE = ['BASE', 'ACTUAL', 'ALL_OTHER', 'UNMODELED', 'PREDICTION'];
export const CoefficientV2Component = ({
  children,
  reportName: reportId,
  enabled,
  filterSelection,
  coefficientHelpData,
}: CoefficientComponentProps) => {
  const { data, isFetching } = useDataProvider<Record<string, any>>(
    reportId,
    filterSelection,
    [{ id: 'WATERFALL_ORDER', desc: false }],
    enabled,
  );

  const coefficientHelpMap: Record<string, CoefficientHelp> = useMemo(() => {
    if (coefficientHelpData) {
      const { rows } = coefficientHelpData;
      const ret: Record<string, CoefficientHelp> = {};
      for (let index = 0; index < rows.length; index++) {
        const row = rows[index];
        ret[row['WATERFALL_NAME']] = {
          name: row['WATERFALL_NAME_DESC'],
          description: row['WATERFALL_DESCRIPTION'],
        };
      }
      return ret;
    }
    return {};
  }, [coefficientHelpData]);

  const {
    setSelectedValue: setCoeffSelectorValue,
    selectedValue: coeffSelectorValue,
  } = useAppFilterSelectedValue(FILTER_NAMES.COEFFICIENTSELECTOR);
  const variableCompBrand = useAppFilterSelectedValue(
    FILTER_NAMES.VARIABLECOMPBRAND,
  );
  const [selectedCompBrandId, setSelectedCompBrandId] = useState<
    string[] | undefined
  >();
  const [filterValues, setFilteredValues] = useState<any[]>();

  useEffect(() => {
    // Perform this only once.
    if (!data || filterValues) return undefined;
    // Collect all values to populate the filter.
    const values = data.rows.flatMap((i) => {
      if (i.brands) {
        return i.brands.map((b: any) => ({
          value: b.id,
          label: `${i.name} : ${b.name}`,
        }));
      } else {
        return [{ value: i.id, label: i.name }];
      }
    });

    // Filter values starting with "Comp " based on VARIABLECOMPBRAND
    const _filterValues = values.filter((val) => {
      // Other cards that we don't want to display by default.
      if (TO_REMOVE.indexOf(val.value) !== -1) {
        const variableCompBrandValue =
          variableCompBrand.selectedValue?.values ?? [];
        const foundInVariableCompBrand = variableCompBrandValue.find(
          (i) => i.value === val.value,
        );
        return foundInVariableCompBrand !== undefined;
      }
      if (val.label.startsWith('Comp ')) {
        // Check if it should be kept based on VARIABLECOMPBRAND
        const variableCompBrandValue =
          variableCompBrand.selectedValue?.values ?? [];
        const foundInVariableCompBrand = variableCompBrandValue.find(
          (compVal) => compVal.value === val.value,
        );
        return foundInVariableCompBrand !== undefined; // Keep if it's in VARIABLECOMPBRAND
      }
      return true; // Keep if it doesn't start with "Comp "
    });

    setFilteredValues(_filterValues);
  }, [data, variableCompBrand, filterValues]);
  useEffect(() => {
    if (filterValues) {
      // Update selected values in coeffSelectorValue
      setCoeffSelectorValue({
        id: undefined,
        values: filterValues,
        matchStrategy,
      });
    }
  }, [filterValues, setCoeffSelectorValue]);

  const onClickCardHandler = useCallback(
    (col: Record<string, any>, compBrand?: Record<string, any>) => {
      const {
        selectedValue: variableCompBrandValue,
        setSelectedValue: setVariableCompBrandValue,
      } = variableCompBrand;
      if (variableCompBrandValue) {
        let value = { value: '', label: '' };
        if (compBrand) {
          value = {
            value: compBrand.id,
            label: `${col.name} : ${compBrand.name}`,
          };
        } else {
          value = { value: col.id, label: col.name };
        }
        setVariableCompBrandValue({
          ...variableCompBrandValue,
          values: [value],
        });
      }
    },
    [variableCompBrand],
  );

  const sortFunction = useCallback(
    (a: Record<string, any>, b: Record<string, any>) => {
      const val = hasFilterSelectedValue(coeffSelectorValue);
      if (val) {
        const { values } = val;
        const indexA = values.findIndex((i) => a.id === i.value);
        const indexB = values.findIndex((i) => b.id === i.value);
        return indexA - indexB;
      }
      return 1;
    },
    [coeffSelectorValue],
  );

  const filterBrandFunction = useCallback(
    (b: Record<string, any>) => {
      const val = hasFilterSelectedValue(coeffSelectorValue);
      if (val) {
        const { values } = val;
        if (b) {
          const found = values.find((i) => i.value === b.id);
          if (found) {
            return true;
          }
        }
      }
      return false;
    },
    [coeffSelectorValue],
  );
  const filterFunction = useCallback(
    (a: Record<string, any>) => {
      const val = hasFilterSelectedValue(coeffSelectorValue);
      if (val) {
        const { values } = val;
        const indexA = values.findIndex((i) => a.id === i.value);
        if (indexA === -1 && a.brands) {
          const found = values.find((i) =>
            a.brands.find((b: any) => i.value === b.id),
          );
          if (found) {
            return true;
          }
        }
        return indexA !== -1;
      }
      return false;
    },
    [coeffSelectorValue],
  );

  const isActive = useCallback(
    (col: Record<string, any>) => {
      const { selectedValue } = variableCompBrand;
      const val = hasFilterSelectedValue(selectedValue);
      if (val) {
        const { values } = val;
        const found = values.find((i) => i.value === col.id);
        return found !== undefined;
      }
      return false;
    },
    [variableCompBrand],
  );

  useEffect(() => {
    const { selectedValue } = variableCompBrand;
    const val = hasFilterSelectedValue(selectedValue);
    if (val) {
      const { values } = val;
      if (values && values.length > 0) {
        const compB = values.map((i) => i.value);
        setSelectedCompBrandId(compB);
      }
    }
  }, [variableCompBrand]);
  const [coefCol, setCoefCol] = useState<Record<string, any> | undefined>();
  const [dragOverCol, setDragOverCol] = useState<
    Record<string, any> | undefined
  >();

  const handleDragStart = useCallback((e: any, col: Record<string, any>) => {
    setCoefCol(col);
  }, []);
  const handleDragEnd = useCallback(() => {}, []);
  const handleDragOver = useCallback((e: any, overCol: Record<string, any>) => {
    setDragOverCol(overCol);
    e.preventDefault();
  }, []);
  const handleDrop = useCallback(
    (e: any, dropCol: Record<string, any>) => {
      if (!coefCol) return;
      if (coeffSelectorValue) {
        const values = coeffSelectorValue.values.concat();
        const currentIndex = values.findIndex((i) => i.value === coefCol.id);
        const targetIndex = values.findIndex((i) => i.value === dropCol.id);

        if (currentIndex !== -1 && targetIndex !== -1) {
          const item = values.splice(currentIndex, 1)[0];
          values.splice(targetIndex, 0, item);
        }
        setCoeffSelectorValue({
          ...coeffSelectorValue,
          values,
        });
      }
      setCoefCol(undefined);
      setDragOverCol(undefined);
    },
    [coeffSelectorValue, setCoeffSelectorValue, coefCol],
  );
  return (
    <LoadingComponent
      isLoading={!data}
      noDataAvailable={data?.rows && data?.rows.length === 0 && !isFetching}
      message={''}
    >
      <div className="coefficient-wrapper">
        <BackToDemandComponent />
        <div className="fw-bold pb-3 d-flex">
          Select Coefficient
          {isFetching && (
            <div className="ms-auto">
              <PreloaderComponent message="" />
            </div>
          )}
        </div>
        <div className="d-flex">
          <div className="d-flex">{children}</div>
        </div>
        <div className="coefficient-cards-container">
          {data?.rows
            .concat()
            .sort(sortFunction)
            .filter(filterFunction)
            .map((row, index) => (
              <div
                key={index}
                className={`coefficient-card ${row.brands ? 'comp' : ''} ${coefCol === row ? 'dragging' : ''} ${dragOverCol === row ? 'dragging-over' : ''}`}
                draggable={true}
                onDragStart={(e) => handleDragStart(e, row)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, row)}
                onDrop={(e) => handleDrop(e, row)}
              >
                <CoefficientCardComponent
                  onClick={() =>
                    onClickCardHandler(
                      row,
                      row.brands && row.brands.length > 0
                        ? row.brands[0]
                        : undefined,
                    )
                  }
                  onCompBrandClick={(compBrand) =>
                    onClickCardHandler(row, compBrand)
                  }
                  selectedBrand={selectedCompBrandId}
                  coefficientHelp={coefficientHelpMap[row['WATERFALL_NAME']]}
                  active={isActive(row)}
                  title={row.name}
                  brands={row.brands?.filter(filterBrandFunction)}
                  value={row.value}
                />
              </div>
            ))}
        </div>
      </div>
    </LoadingComponent>
  );
};
